import React from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";

/**
 * Minimal React error boundary. Catches render-time errors in any page rendered
 * inside <Layout> and shows a graceful "Something went wrong — reload" screen
 * instead of a blank white view. Errors are also console.error'd so devs still
 * see the stack trace.
 */
export default class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, message: "" };
    }
    static getDerivedStateFromError(err) {
        return { hasError: true, message: err?.message || String(err) };
    }
    componentDidCatch(err, info) {
        // Preserve stack in devtools console.
        console.error("[ErrorBoundary]", err, info);
    }
    componentDidUpdate(prevProps) {
        // Reset when the routed page changes (children identity flips).
        if (prevProps.resetKey !== this.props.resetKey && this.state.hasError) {
            this.setState({ hasError: false, message: "" });
        }
    }
    render() {
        if (!this.state.hasError) return this.props.children;
        return (
            <div className="mx-auto max-w-xl px-6 py-16 text-center" data-testid="error-boundary-fallback">
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-red-50 text-[#E02B2B] mb-3">
                    <AlertTriangle className="w-7 h-7" />
                </div>
                <h1 className="font-display text-2xl font-bold text-[#1E3355]">Something went wrong</h1>
                <p className="mt-2 text-sm text-gray-500">
                    This page hit an unexpected error. Reload to try again — your data is safe.
                </p>
                {this.state.message && (
                    <pre className="mt-4 mx-auto max-w-md text-left text-[11px] text-gray-500 bg-[#F4F6F9] rounded-md p-3 whitespace-pre-wrap break-words" data-testid="error-boundary-message">{this.state.message}</pre>
                )}
                <button
                    onClick={() => window.location.reload()}
                    className="mt-6 inline-flex items-center gap-1.5 rounded-md bg-[#1E3355] text-white px-4 py-2 text-sm font-medium hover:opacity-90"
                    data-testid="error-boundary-reload"
                >
                    <RefreshCw className="w-4 h-4" /> Reload
                </button>
            </div>
        );
    }
}
